import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft, Paperclip, Sparkles, RefreshCw, Send, CheckCircle2,
  UserPlus, ListPlus, Wand2, Minimize2, Maximize2, Download,
} from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, streamPost, CATEGORY_COLORS, PRIORITY_META, timeAgo } from "../../lib/api";
import { useCredits } from "../../lib/useCredits";
import { LeaOrb } from "../../components/Lea";

const TONES = ["professionnel", "chaleureux", "direct", "commercial", "formel"];
const SENTIMENT_META = { positif: "text-success", neutre: "text-muted-foreground", négatif: "text-destructive" };

export default function EmailDetail() {
  const { t } = useTranslation();
  const { emailId } = useParams();
  const navigate = useNavigate();
  const credits = useCredits();
  const isPaid = credits?.mode === "quota";
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [tone, setTone] = useState("professionnel");
  const [busy, setBusy] = useState("");
  const [sent, setSent] = useState(false);
  const toneLabels = t("emailDetail.tones");

  const load = () => api.get(`/emails/${emailId}`).then((r) => {
    setData(r.data);
    if (r.data.suggested_reply) setReply(r.data.suggested_reply);
  }).catch((e) => { toast.error(fmtErr(e)); navigate("/app/inbox"); });

  useEffect(() => { load(); }, [emailId]);

  const genReply = async (mode = "new") => {
    const current = reply;
    setBusy(mode);
    setReply("");
    try {
      await streamPost(`/emails/${emailId}/reply/stream`,
        { tone, mode, current_text: mode === "new" ? "" : current },
        (delta) => setReply((r) => r + delta), crypto.randomUUID());
      setSent(false);
    } catch (e) { toast.error(fmtErr(e, t("emailDetail.replyFail"))); }
    setBusy("");
  };

  const send = async () => {
    if (!reply.trim()) return;
    setBusy("send");
    try {
      await api.post(`/emails/${emailId}/send`, { text: reply, confirm: true });
      setSent(true);
      toast.success(t("emailDetail.sentDemo"));
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy("");
  };

  const analyzeEmail = async () => {
    setBusy("analyze");
    try {
      const { data: res } = await api.post(`/emails/${emailId}/reanalyze`, {},
        { headers: { "Idempotency-Key": crypto.randomUUID() } });
      setData((d) => ({ ...d, analysis: res.analysis, analysis_source: res.analysis_source }));
      toast.success(t("emailDetail.analysisReady"));
    } catch (e) { toast.error(fmtErr(e, t("emailDetail.analyzeFail"))); }
    setBusy("");
  };

  const createLead = async () => {
    setBusy("lead");
    try {
      await api.post(`/emails/${emailId}/create-lead`);
      toast.success(t("emailDetail.leadCreated"));
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy("");
  };

  const createTask = async (title) => {
    try {
      await api.post(`/emails/${emailId}/tasks`, { title });
      toast.success(t("emailDetail.taskCreated"));
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const markDone = async () => {
    await api.patch(`/emails/${emailId}`, { status: "termine" });
    toast.success(t("emailDetail.markedDone"));
    navigate("/app/inbox");
  };

  const openAttachment = async (index) => {
    setBusy(`att-${index}`);
    try {
      const res = await api.get(`/emails/${emailId}/attachments/${index}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank", "noopener");
    } catch (e) {
      toast.error(t("emailDetail.attachmentError"));
    }
    setBusy("");
  };

  const formatSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  if (!data) {
    return (
      <div className="space-y-4" data-testid="email-loading">
        <div className="skeleton-shimmer h-8 w-48 rounded-lg" />
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 skeleton-shimmer h-96 rounded-2xl" />
          <div className="lg:col-span-2 skeleton-shimmer h-96 rounded-2xl" />
        </div>
      </div>
    );
  }

  const { email, analysis, lead } = data;
  const pr = PRIORITY_META[analysis?.priority] || PRIORITY_META.normale;

  return (
    <div className="space-y-6" data-testid="email-detail-page">
      <button onClick={() => navigate("/app/inbox")} data-testid="back-to-inbox"
        className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft size={14} /> {t("emailDetail.back")}
      </button>

      <div className="grid lg:grid-cols-5 gap-6 items-start">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-3 rounded-2xl border border-border bg-card p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <h1 className="font-display font-bold text-xl tracking-tight" data-testid="email-subject">{email.subject}</h1>
              <p className="text-xs text-muted-foreground mt-1.5">
                <strong className="text-foreground">{email.from_name}</strong> &lt;{email.from_email}&gt; → {email.to} · {timeAgo(email.date)}
              </p>
            </div>
            <span className={`text-[10px] font-semibold rounded-full px-2.5 py-1 shrink-0 ${CATEGORY_COLORS[email.category] || CATEGORY_COLORS.Autre}`}>
              {email.category}
            </span>
          </div>
          <div className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90 border-l-2 border-border pl-5" data-testid="email-body">
            {email.body}
          </div>
          {email.attachments?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2" data-testid="email-attachments">
              {email.attachments.map((a, i) => {
                const att = typeof a === "string" ? { filename: a } : a;
                return (
                  <button key={i} onClick={() => openAttachment(i)} disabled={busy === `att-${i}`}
                    data-testid={`attachment-open-${i}`} title={t("emailDetail.openAttachment")}
                    className="flex items-center gap-2 rounded-lg border border-border bg-secondary/50 px-3 py-2 text-xs font-medium hover:bg-secondary hover:border-foreground/20 transition-colors disabled:opacity-50">
                    <Paperclip size={12} className="shrink-0" />
                    <span className="truncate max-w-[220px]">{att.filename}</span>
                    {att.size > 0 && <span className="text-muted-foreground shrink-0">{formatSize(att.size)}</span>}
                    <Download size={12} className="text-muted-foreground shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={markDone} data-testid="mark-done-btn"
              className="rounded-full border border-success/40 text-success text-xs font-bold px-4 py-2 hover:bg-success/10 transition-colors flex items-center gap-1.5">
              <CheckCircle2 size={13} /> {t("emailDetail.markDone")}
            </button>
            {analysis?.lead && !lead && (
              <button onClick={createLead} disabled={busy === "lead"} data-testid="create-lead-btn"
                className="rounded-full border border-electric/40 text-electric text-xs font-bold px-4 py-2 hover:bg-electric/10 transition-colors flex items-center gap-1.5 disabled:opacity-50">
                <UserPlus size={13} /> {t("emailDetail.createLead")}
              </button>
            )}
            {lead && (
              <button onClick={() => navigate("/app/prospects")} data-testid="view-lead-btn"
                className="rounded-full bg-electric/10 text-electric text-xs font-bold px-4 py-2 hover:bg-electric/20 transition-colors flex items-center gap-1.5">
                <UserPlus size={13} /> {t("emailDetail.viewLead")}
              </button>
            )}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-electric/25 bg-card p-6" data-testid="ai-analysis-panel">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2.5">
                <LeaOrb size={26} />
                <p className="font-display font-bold text-sm">{t("emailDetail.analysis")}</p>
              </div>
              {analysis && (
                <button onClick={analyzeEmail} disabled={!!busy} data-testid="regenerate-analysis-btn"
                  title={t("emailDetail.regenerateAnalysis")}
                  className="text-[11px] font-bold text-electric hover:underline disabled:opacity-50 flex items-center gap-1">
                  <RefreshCw size={11} className={busy === "analyze" ? "animate-spin" : ""} />
                  {isPaid ? t("emailDetail.regenerateAnalysisPlain") : t("emailDetail.regenerateAnalysis")}
                </button>
              )}
            </div>
            {analysis ? (
              <dl className="space-y-4 text-sm">
                <div><dt className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">{t("emailDetail.summary")}</dt>
                  <dd className="leading-relaxed" data-testid="analysis-summary">{analysis.summary}</dd></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><dt className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">{t("emailDetail.intent")}</dt>
                    <dd className="font-medium">{analysis.intent}</dd></div>
                  <div><dt className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">{t("emailDetail.sentiment")}</dt>
                    <dd className={`font-medium capitalize ${SENTIMENT_META[analysis.sentiment] || ""}`}>{analysis.sentiment}</dd></div>
                  <div><dt className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">{t("emailDetail.priority")}</dt>
                    <dd className={`font-semibold flex items-center gap-1.5 ${pr.text}`}><span className={`h-2 w-2 rounded-full ${pr.dot}`} />{t(`priorities.${analysis.priority || "normale"}`)}</dd></div>
                  <div><dt className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">{t("emailDetail.category")}</dt>
                    <dd className="font-medium">{analysis.category}</dd></div>
                </div>
                {analysis.suggested_actions?.length > 0 && (
                  <div><dt className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">{t("emailDetail.actions")}</dt>
                    <dd className="space-y-1.5">
                      {analysis.suggested_actions.map((a, i) => (
                        <p key={i} className="text-xs flex items-start gap-2"><Sparkles size={11} className="text-electric mt-0.5 shrink-0" />{a}</p>
                      ))}
                    </dd></div>
                )}
                {analysis.tasks?.length > 0 && (
                  <div><dt className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">{t("emailDetail.tasks")}</dt>
                    <dd className="space-y-2">
                      {analysis.tasks.map((task, i) => (
                        <div key={i} className="flex items-center justify-between gap-2 rounded-lg bg-secondary/60 px-3 py-2">
                          <span className="text-xs font-medium">{task.title}{task.due_hint ? ` — ${task.due_hint}` : ""}</span>
                          <button onClick={() => createTask(task.title)} data-testid={`create-task-${i}`}
                            className="text-[10px] font-bold text-electric hover:underline shrink-0 flex items-center gap-1">
                            <ListPlus size={11} /> {t("emailDetail.create")}
                          </button>
                        </div>
                      ))}
                    </dd></div>
                )}
              </dl>
            ) : (
              <div className="text-center py-3">
                <p className="text-xs text-muted-foreground mb-4">{t("emailDetail.analysisUnavailable")}</p>
                <button onClick={analyzeEmail} disabled={!!busy} data-testid="analyze-email-btn"
                  className="rounded-full bg-electric text-white px-5 py-2.5 text-xs font-bold hover:bg-blue-700 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                  <Sparkles size={12} className={busy === "analyze" ? "animate-spin" : ""} />
                  {isPaid ? t("emailDetail.analyzeWithAiPlain") : t("emailDetail.analyzeWithAi")}
                </button>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-border bg-card p-6" data-testid="reply-panel">
            <div className="flex items-center justify-between mb-4">
              <p className="font-display font-bold text-sm flex items-center gap-2">
                <Wand2 size={14} className="text-electric" /> {t("emailDetail.replyTitle")}
              </p>
              <button onClick={() => genReply("new")} disabled={!!busy} data-testid="generate-reply-btn"
                className="text-[11px] font-bold text-electric hover:underline disabled:opacity-50 flex items-center gap-1">
                <RefreshCw size={11} className={busy && busy !== "send" ? "animate-spin" : ""} />
                {reply ? t("emailDetail.regenerate") : t("emailDetail.generate")}
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {TONES.map((toneOpt, i) => (
                <button key={toneOpt} onClick={() => setTone(toneOpt)} data-testid={`reply-tone-${toneOpt}`}
                  className={`rounded-full px-3 py-1 text-[10px] font-bold border transition-colors ${tone === toneOpt ? "bg-electric text-white border-electric" : "border-border hover:border-electric/50"}`}>
                  {toneLabels[i]}
                </button>
              ))}
            </div>
            {busy && busy !== "send" ? (
              <div className="space-y-2 py-2">
                <p className="text-[11px] text-muted-foreground animate-pulse-soft">{t("emailDetail.analyzing")}</p>
                <div className="skeleton-shimmer h-3 rounded w-full" />
                <div className="skeleton-shimmer h-3 rounded w-5/6" />
                <div className="skeleton-shimmer h-3 rounded w-4/6" />
              </div>
            ) : (
              <textarea value={reply} onChange={(e) => { setReply(e.target.value); setSent(false); }} rows={8}
                placeholder={t("emailDetail.replyPlaceholder")}
                data-testid="reply-textarea"
                className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-electric focus:ring-2 focus:ring-electric/20 transition-shadow resize-y" />
            )}
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <button onClick={() => genReply("shorter")} disabled={!reply || !!busy} data-testid="reply-shorten"
                className="rounded-full border border-border text-[11px] font-semibold px-3 py-1.5 hover:border-electric/50 transition-colors disabled:opacity-40 flex items-center gap-1">
                <Minimize2 size={11} /> {t("emailDetail.shorten")}
              </button>
              <button onClick={() => genReply("longer")} disabled={!reply || !!busy} data-testid="reply-expand"
                className="rounded-full border border-border text-[11px] font-semibold px-3 py-1.5 hover:border-electric/50 transition-colors disabled:opacity-40 flex items-center gap-1">
                <Maximize2 size={11} /> {t("emailDetail.expand")}
              </button>
              <button onClick={send} disabled={!reply.trim() || !!busy || sent} data-testid="reply-send"
                className="ml-auto rounded-full bg-electric text-white text-[11px] font-bold px-5 py-2 hover:bg-blue-700 transition-colors disabled:opacity-40 flex items-center gap-1.5">
                <Send size={12} /> {sent ? t("emailDetail.sent") : busy === "send" ? t("emailDetail.sending") : t("emailDetail.send")}
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-3">{t("emailDetail.noAutoSend")}</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
